import { styled } from '@mui/material/styles';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Paper, Typography, Box } from '@mui/material';

import { formatMoney, formatBrDate } from '../../shared/formatters';
import { goTo } from '../../shared/utils';

const StyledPaper = styled(Paper)(({ theme }) => ({
    width: '100%',
    overflowX: 'auto',
    marginTop: theme.spacing(3),
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const StyledTable = styled(Table)(({ theme }) => ({
    minWidth: 600,
    width: '100%',
    tableLayout: 'fixed',
}));

const DATE_COLUMN_WIDTH = '110px';
const ACCOUNT_COLUMN_WIDTH = '180px';
const VALUE_COLUMN_WIDTH = '140px';

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '0.938rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
}));

const SectionSubtitle = styled('span')(({ theme }) => ({
    fontWeight: 500,
    textTransform: 'none',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: 'rgba(0, 98, 105, 0.05)',
    '& .MuiTableCell-head': {
        fontWeight: 700,
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: 'rgba(0, 98, 105, 0.04)',
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.938rem',
    fontWeight: 500,
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const DateCell = styled(StyledTableCell)(({ theme }) => ({
    fontWeight: 600,
    color: 'rgb(0,98,105)',
    whiteSpace: 'nowrap',
}));

const MoneyCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.938rem',
    fontWeight: 500,
    padding: '16px',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

/**
 * Sorts transactions chronologically, keeping the original order for
 * transactions created on the same day.
 */
export function sortTransactionsByDate(transactions) {
    return [...(transactions || [])].sort((first, second) => {
        if (first.date === second.date) return (first.id || 0) - (second.id || 0);
        return first.date < second.date ? -1 : 1;
    });
}

const TransactionList = ({ title, monthKey, transactions, isDebit, navigate, showAccountColumn: showAccountColumnProp }) => {
    const items = sortTransactionsByDate(transactions);
    const total = items.reduce((sum, transaction) => sum + (parseFloat(transaction.value) || 0), 0);
    const accountKeys = new Set(
        items.map(transaction => transaction.financeAccount?.id ?? transaction.financeAccount?.name ?? '')
    );
    const showAccountColumn = showAccountColumnProp !== undefined ? showAccountColumnProp : accountKeys.size > 1;
    const columnCount = showAccountColumn ? 4 : 3;

    return (
        <StyledPaper>
            <SectionTitle>
                {title}
                <SectionSubtitle>{monthKey}</SectionSubtitle>
            </SectionTitle>
            <StyledTable>
                <colgroup>
                    <col style={{ width: DATE_COLUMN_WIDTH }} />
                    <col />
                    {showAccountColumn && <col style={{ width: ACCOUNT_COLUMN_WIDTH }} />}
                    <col style={{ width: VALUE_COLUMN_WIDTH }} />
                </colgroup>
                <StyledTableHead>
                    <TableRow>
                        <StyledTableCell>Data</StyledTableCell>
                        <StyledTableCell align="left">Descrição</StyledTableCell>
                        {showAccountColumn && <StyledTableCell align="left">Conta</StyledTableCell>}
                        <StyledTableCell align="right">Valor</StyledTableCell>
                    </TableRow>
                </StyledTableHead>
                <TableBody>
                    {items.length === 0 && (
                        <TableRow>
                            <StyledTableCell colSpan={columnCount}>
                                <EmptyState>Nenhuma transação no período</EmptyState>
                            </StyledTableCell>
                        </TableRow>
                    )}
                    {items.map(transaction => (
                        <StyledTableRow
                            key={transaction.id}
                            hover
                            onClick={() => goTo(navigate, `transactions/edit/${transaction.id}`)}
                        >
                            <DateCell scope="row">{formatBrDate(transaction.date)}</DateCell>
                            <StyledTableCell align="left" sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                {transaction.description || 'Sem descrição'}
                            </StyledTableCell>
                            {showAccountColumn && (
                                <StyledTableCell align="left">
                                    {transaction.financeAccount ? transaction.financeAccount.name : '-'}
                                </StyledTableCell>
                            )}
                            <MoneyCell
                                align="right"
                                sx={{ color: isDebit ? 'error.dark' : 'success.dark', fontWeight: 700 }}
                            >
                                {`${isDebit ? '-' : '+'}${formatMoney(transaction.value)}`}
                            </MoneyCell>
                        </StyledTableRow>
                    ))}
                </TableBody>
                <TableFooter>
                    <StyledTableRow>
                        <StyledTableCell colSpan={columnCount - 1} sx={{ fontWeight: 700, fontSize: '1rem' }}>
                            {`Total ${title}`}
                        </StyledTableCell>
                        <MoneyCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                            {formatMoney(total)}
                        </MoneyCell>
                    </StyledTableRow>
                </TableFooter>
            </StyledTable>
        </StyledPaper>
    );
};

export default TransactionList;
